import React, { useState, useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Lock, ExternalLink, Settings2, Save, Loader2, AlertTriangle, Users, Receipt, DollarSign } from 'lucide-react';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import ExpenseLedgerModal from '../components/ops/ExpenseLedgerModal';
import RevenueLedgerModal from '../components/ops/RevenueLedgerModal';
import EconomicsModal, { computeEconomics } from '../components/ops/EconomicsModal';
import ServiceCommandCenter from '../components/ops/ServiceCommandCenter';
import MrrCommandCenter from '../components/ops/MrrCommandCenter';
import StaffModal from '../components/ops/StaffModal';
import HamburgerMenu from '../components/layout/HamburgerMenu';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';

// ── Inline text/number cell (reused pattern) ──
function EditableCell({ value, onSave, id, field, type = 'text', className = '' }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const [saving, setSaving] = useState(false);
  const ref = useRef(null);

  useEffect(() => { setDraft(value ?? ''); }, [value]);
  useEffect(() => { if (editing && ref.current) { ref.current.focus(); ref.current.select(); } }, [editing]);

  const save = async () => {
    const v = type === 'number' ? (parseFloat(draft) || 0) : draft;
    if (v === value) { setEditing(false); return; }
    setSaving(true);
    try { await onSave(id, field, v); setEditing(false); } catch {} finally { setSaving(false); }
  };

  const handleKey = (e) => { if (e.key === 'Enter') { e.preventDefault(); save(); } if (e.key === 'Escape') { setDraft(value ?? ''); setEditing(false); } };

  if (editing) {
    return (
      <input ref={ref} value={draft}
        onChange={(e) => setDraft(e.target.value)} onKeyDown={handleKey} onBlur={save}
        disabled={saving}
        className={`text-sm px-1.5 py-0.5 border rounded bg-white focus:outline-none focus:ring-2 focus:ring-ring w-full ${type === 'number' ? 'text-right' : ''} ${className}`}
        inputMode={type === 'number' ? 'decimal' : undefined}
      />
    );
  }

  const display = type === 'number'
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(parseFloat(value) || 0)
    : (value || '\u2014');

  return (
    <span onClick={() => setEditing(true)} className={`text-sm cursor-pointer hover:bg-muted/50 rounded px-1.5 py-0.5 -mx-1.5 block transition-colors ${type === 'number' ? 'text-right font-medium' : ''} ${className}`}
      data-role={type === 'number' ? 'mono' : undefined} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && setEditing(true)}>
      {display}
    </span>
  );
}

// ── Inline date cell ──
function EditableDateCell({ value, onSave, id, field }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (value) {
      try { setDraft(format(new Date(value), 'yyyy-MM-dd')); } catch { setDraft(''); }
    } else { setDraft(''); }
  }, [value]);

  useEffect(() => { if (editing && ref.current) ref.current.focus(); }, [editing]);

  const save = async () => {
    if (!draft) { setEditing(false); return; }
    const iso = new Date(draft + 'T00:00:00Z').toISOString();
    setSaving(true);
    try { await onSave(id, field, iso); setEditing(false); } catch {} finally { setSaving(false); }
  };

  if (editing) {
    return <input ref={ref} type="date" value={draft} onChange={(e) => setDraft(e.target.value)}
      onBlur={save} onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
      disabled={saving} className="text-sm px-1 py-0.5 border rounded bg-white focus:outline-none focus:ring-2 focus:ring-ring w-[130px]" />;
  }

  const display = value ? (() => { try { return format(new Date(value), 'MM/dd/yy'); } catch { return '\u2014'; } })() : '\u2014';
  return <span onClick={() => setEditing(true)} className="text-sm cursor-pointer hover:bg-muted/50 rounded px-1 py-0.5 -mx-1 block transition-colors" data-role="mono" role="button" tabIndex={0}>{display}</span>;
}

// ── Labor Breakdown Modal ──
function LaborModal({ open, onOpenChange, project, onResync }) {
  const assignees = Array.isArray(project?.assignee_data) ? project.assignee_data : [];
  const [syncing, setSyncing] = useState(false);
  const fmt = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(parseFloat(v) || 0);
  const totalHours = assignees.reduce((s, a) => s + (a.assigned_hours || 0), 0);
  const totalCost = assignees.reduce((s, a) => s + (a.cost || 0), 0);

  const handleResync = async () => {
    if (!project?.id) return;
    setSyncing(true);
    try {
      await onResync(project.id);
      toast.success('Labor data re-synced from ClickUp');
    } catch { toast.error('Re-sync failed'); }
    finally { setSyncing(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle data-role="heading">Labor Breakdown: {project?.project_name}</DialogTitle>
        </DialogHeader>
        <div className="py-3">
          {assignees.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No tracked time data available. Click Re-sync to fetch from ClickUp.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs font-semibold uppercase">Name</TableHead>
                  <TableHead className="text-right text-xs font-semibold uppercase">Hours</TableHead>
                  <TableHead className="text-right text-xs font-semibold uppercase">Rate</TableHead>
                  <TableHead className="text-right text-xs font-semibold uppercase">Total Cost</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {assignees.map((a, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-sm font-medium">{a.username || 'Unknown'}</TableCell>
                    <TableCell className="text-right text-sm" data-role="mono">{(a.assigned_hours ?? 0).toFixed(2)}</TableCell>
                    <TableCell className="text-right text-sm" data-role="mono">{fmt(a.pay_rate ?? 0)}/hr</TableCell>
                    <TableCell className="text-right text-sm font-semibold" data-role="mono">{fmt(a.cost ?? 0)}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="border-t-2">
                  <TableCell className="text-sm font-semibold">Total</TableCell>
                  <TableCell className="text-right text-sm font-semibold" data-role="mono">{totalHours.toFixed(2)}</TableCell>
                  <TableCell />
                  <TableCell className="text-right text-sm font-bold" data-role="mono">{fmt(totalCost)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" size="sm" onClick={handleResync} disabled={syncing}>
            {syncing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            Re-sync from ClickUp
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main OpsPage ──
export default function OpsPage() {
  const { session } = useAuth();
  const [opsAuthed, setOpsAuthed] = useState(false);
  const [password, setPassword] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [trackKey, setTrackKey] = useState('');
  const [maskedKey, setMaskedKey] = useState('');
  const [savingKey, setSavingKey] = useState(false);
  const [laborProject, setLaborProject] = useState(null);
  const [expenseProject, setExpenseProject] = useState(null);
  const [revenueProject, setRevenueProject] = useState(null);
  const [economicsProject, setEconomicsProject] = useState(null);
  const [staffOpen, setStaffOpen] = useState(false);
  const [webDays, setWebDays] = useState('30');

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    try {
      await api.post('/api/ops/auth', { password });
      setOpsAuthed(true);
      sessionStorage.setItem('ops_auth', 'true');
    } catch { toast.error('Invalid password'); } finally { setAuthLoading(false); }
  };

  useEffect(() => { if (sessionStorage.getItem('ops_auth') === 'true') setOpsAuthed(true); }, []);

  const loadProjects = useCallback(async () => {
    try { setLoading(true); const res = await api.get('/api/ops/projects'); setProjects(res.data.data || []); }
    catch { toast.error('Failed to load projects'); } finally { setLoading(false); }
  }, []);

  const loadTrackKey = useCallback(async () => {
    try { const res = await api.get('/api/settings/trackabi-key'); setMaskedKey(res.data.masked_key); } catch {}
  }, []);

  useEffect(() => {
    if (opsAuthed && session?.access_token) { loadProjects(); loadTrackKey(); }
  }, [opsAuthed, session, loadProjects, loadTrackKey]);

  const handleUpdateField = async (id, field, value) => {
    try {
      const res = await api.patch(`/api/ops/projects/${id}`, { [field]: value });
      setProjects(prev => prev.map(p => p.id === id ? res.data.data : p));
      toast.success('Updated');
    } catch { toast.error('Failed to update'); throw new Error(); }
  };

  const handleSaveExpenses = async (id, expenseBreakdown) => {
    try {
      const res = await api.patch(`/api/ops/projects/${id}`, { expense_breakdown: expenseBreakdown });
      setProjects(prev => prev.map(p => p.id === id ? res.data.data : p));
      toast.success('Expenses saved — cost & profit updated');
    } catch { toast.error('Failed to save expenses'); throw new Error(); }
  };

  const handleSaveRevenue = async (id, revenueBreakdown, salesCommission) => {
    try {
      const payload = { revenue_breakdown: revenueBreakdown };
      if (salesCommission) payload.sales_commission = salesCommission;
      const res = await api.patch(`/api/ops/projects/${id}`, payload);
      setProjects(prev => prev.map(p => p.id === id ? res.data.data : p));
      toast.success('Revenue & commission saved');
    } catch { toast.error('Failed to save revenue'); throw new Error(); }
  };

  const handleSaveTrackKey = async () => {
    setSavingKey(true);
    try {
      const res = await api.post('/api/settings/trackabi-key', { trackabi_api_key: trackKey });
      setMaskedKey(res.data.masked_key); setTrackKey(''); setSettingsOpen(false);
      toast.success('Trackabi API key updated');
    } catch { toast.error('Failed to update key'); } finally { setSavingKey(false); }
  };

  // Filter + sort projects by end_date
  const filteredProjects = (() => {
    let list = [...projects];
    if (webDays !== 'all') {
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - Number(webDays));
      list = list.filter(p => {
        if (!p.end_date) return true; // keep projects without end date
        try { return new Date(p.end_date) >= cutoff; } catch { return true; }
      });
    }
    // Sort by end_date descending (most recent first), nulls last
    list.sort((a, b) => {
      const da = a.end_date ? new Date(a.end_date).getTime() : 0;
      const db = b.end_date ? new Date(b.end_date).getTime() : 0;
      return db - da;
    });
    return list;
  })();

  const profitBadge = (pct) => {
    const n = parseFloat(pct) || 0;
    const warn = n < 50;
    const cls = n >= 70 ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : n >= 50 ? 'bg-yellow-50 text-yellow-700 border-yellow-200'
      : 'bg-red-50 text-red-700 border-red-200';
    return (
      <span className="inline-flex items-center gap-1">
        <Badge variant="outline" className={`${cls} text-xs tabular-nums`}>{n.toFixed(1)}%</Badge>
        {warn && <AlertTriangle className="h-3.5 w-3.5 text-red-500" />}
      </span>
    );
  };

  // ── Password gate ──
  if (!opsAuthed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <Card className="w-full max-w-sm shadow-[var(--shadow-2)]">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-muted-foreground" />
              <h2 className="text-lg font-semibold" data-role="heading">Operations Access</h2>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordSubmit} className="space-y-3">
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password" autoFocus data-testid="ops-password-input" />
              <Button type="submit" className="w-full" disabled={authLoading || !password}>
                {authLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}Unlock
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Main view ──
  return (
    <div className="min-h-screen bg-background" data-testid="ops-page">
      <header className="sticky top-0 z-50 w-full border-b bg-card/95 backdrop-blur">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <h1 className="text-lg font-semibold tracking-tight" data-role="heading">Project Command Center</h1>
          <div className="flex items-center gap-2">
            {maskedKey && <span className="text-xs text-muted-foreground hidden sm:inline" data-role="mono">Trackabi: {maskedKey}</span>}
            <Button variant="ghost" size="sm" onClick={() => setStaffOpen(true)}><Users className="h-4 w-4" /></Button>
            <Button variant="ghost" size="sm" onClick={() => setSettingsOpen(true)}><Settings2 className="h-4 w-4" /></Button>
            <HamburgerMenu />
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        <Card className="shadow-[var(--shadow-1)]">
          <CardHeader className="pb-2 pt-4 px-4 sm:px-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-semibold tracking-tight" data-role="heading">Websites</h2>
                <Badge variant="secondary" className="text-xs">{filteredProjects.length} project{filteredProjects.length !== 1 ? 's' : ''}</Badge>
              </div>
              <select
                value={webDays}
                onChange={(e) => setWebDays(e.target.value)}
                className="text-xs border rounded px-2 py-1 bg-background w-fit"
              >
                <option value="all">All</option>
                <option value="30">30 days</option>
                <option value="60">60 days</option>
                <option value="90">90 days</option>
                <option value="120">120 days</option>
                <option value="365">1 year</option>
              </select>
            </div>
          </CardHeader>
          <CardContent className="px-0 pb-0">
            {loading ? (
              <div className="p-6 space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
            ) : filteredProjects.length === 0 ? (
              <div className="p-12 text-center">
                <p className="text-muted-foreground text-sm">{projects.length > 0 ? 'No projects in this date range' : 'No projects yet'}</p>
                {projects.length === 0 && <p className="text-muted-foreground text-xs mt-1">Projects from the "New Projects" ClickUp list will appear here.</p>}
              </div>
            ) : (
              <div className="overflow-x-auto" data-testid="ops-projects-table">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="min-w-[200px] text-xs font-semibold uppercase tracking-wide text-muted-foreground">Project</TableHead>
                      <TableHead className="w-[110px] text-xs font-semibold uppercase tracking-wide text-muted-foreground">Start</TableHead>
                      <TableHead className="w-[110px] text-xs font-semibold uppercase tracking-wide text-muted-foreground">End</TableHead>
                      <TableHead className="w-[60px] text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground">Days</TableHead>
                      <TableHead className="w-[70px] text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground">Hours</TableHead>
                      <TableHead className="min-w-[120px] text-xs font-semibold uppercase tracking-wide text-muted-foreground">Team</TableHead>
                      <TableHead className="w-[130px] text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground">Expenses</TableHead>
                      <TableHead className="w-[130px] text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground">Revenue</TableHead>
                      <TableHead className="w-[100px] text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground">Profit</TableHead>
                      <TableHead className="w-[120px] text-xs font-semibold uppercase tracking-wide text-muted-foreground">Health</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredProjects.map((p) => {
                      const assignees = Array.isArray(p.assignee_data) ? p.assignee_data.map(a => a.username || a.email || '').filter(Boolean).join(', ') : '';

                      return (
                        <TableRow key={p.id} className="hover:bg-[var(--row-hover)] transition-colors duration-150">
                          {/* Project Name — editable + ClickUp link */}
                          <TableCell>
                            <div className="flex items-center gap-1.5">
                              <EditableCell value={p.project_name} onSave={handleUpdateField} id={p.id} field="project_name" className="flex-1" />
                              {p.task_id && (
                                <a href={`https://app.clickup.com/t/${p.task_id}`} target="_blank" rel="noopener noreferrer"
                                  className="text-muted-foreground hover:text-primary transition-colors flex-shrink-0" title="Open in ClickUp">
                                  <ExternalLink className="h-3.5 w-3.5" />
                                </a>
                              )}
                            </div>
                          </TableCell>
                          {/* Dates — editable date pickers */}
                          <TableCell><EditableDateCell value={p.start_date} onSave={handleUpdateField} id={p.id} field="start_date" /></TableCell>
                          <TableCell><EditableDateCell value={p.end_date} onSave={handleUpdateField} id={p.id} field="end_date" /></TableCell>
                          <TableCell className="text-right"><span className="text-sm" data-role="mono">{p.days_to_complete ?? '\u2014'}</span></TableCell>
                          <TableCell className="text-right"><span className="text-sm font-medium" data-role="mono">{parseFloat(p.tracked_hours || 0).toFixed(1)}</span></TableCell>
                          {/* Assignees — clickable for labor modal */}
                          <TableCell>
                            <button onClick={() => setLaborProject(p)}
                              className="text-sm text-primary hover:underline truncate block max-w-[130px] text-left"
                              title="View labor breakdown">
                              <span className="inline-flex items-center gap-1">
                                <Users className="h-3 w-3 flex-shrink-0" />
                                {assignees || <em className="text-muted-foreground">\u2014</em>}
                              </span>
                            </button>
                          </TableCell>
                          {/* Cost — clickable to open expense ledger */}
                          <TableCell className="text-right">
                            <button
                              onClick={() => setExpenseProject(p)}
                              className="text-sm font-medium text-primary hover:underline cursor-pointer inline-flex items-center gap-1 ml-auto"
                              title="Open expense ledger"
                              data-role="mono"
                            >
                              <Receipt className="h-3 w-3 flex-shrink-0" />
                              {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(parseFloat(p.project_cost) || 0)}
                            </button>
                          </TableCell>
                          {/* Billed — clickable to open revenue ledger */}
                          <TableCell className="text-right">
                            <button
                              onClick={() => setRevenueProject(p)}
                              className="text-sm font-medium text-emerald-700 hover:underline cursor-pointer inline-flex items-center gap-1 ml-auto"
                              title="Open revenue ledger"
                              data-role="mono"
                            >
                              <DollarSign className="h-3 w-3 flex-shrink-0" />
                              {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(parseFloat(p.project_billed) || 0)}
                            </button>
                          </TableCell>
                          {/* Profit */}
                          <TableCell className="text-right">{profitBadge(p.profit_loss)}</TableCell>
                          {/* Health */}
                          <TableCell>
                            {(() => {
                              const ec = computeEconomics(p);
                              const fmtK = (v) => {
                                const n = parseFloat(v) || 0;
                                return n >= 1000 ? `$${(n/1000).toFixed(1)}k` : `$${n.toFixed(0)}`;
                              };
                              return (
                                <button
                                  onClick={() => setEconomicsProject(p)}
                                  className="text-left hover:bg-muted/50 rounded px-1.5 py-1 -mx-1.5 transition-colors cursor-pointer block w-full"
                                  title="View unit economics"
                                >
                                  <div className="text-[10px] text-muted-foreground leading-tight space-y-0.5">
                                    <div className="flex justify-between">
                                      <span>Cash</span>
                                      <span className="font-semibold text-emerald-700" data-role="mono">{fmtK(ec.cashThisMonth)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span>ACV</span>
                                      <span className="font-semibold text-primary" data-role="mono">{fmtK(ec.acv)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span>LTV</span>
                                      <span className="font-semibold text-violet-700" data-role="mono">{fmtK(ec.ltv)}</span>
                                    </div>
                                  </div>
                                </button>
                              );
                            })()}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* MRR Command Center */}
        <div className="mt-6">
          <MrrCommandCenter />
        </div>

        {/* Service Command Center */}
        <div className="mt-6">
          <ServiceCommandCenter onOpenStaff={() => setStaffOpen(true)} />
        </div>
      </main>

      {/* Staff Modal */}
      <StaffModal open={staffOpen} onOpenChange={setStaffOpen} />

      {/* Labor Breakdown Modal */}
      <LaborModal open={!!laborProject} onOpenChange={(o) => { if (!o) setLaborProject(null); }} project={laborProject}
        onResync={async (id) => {
          const res = await api.post(`/api/ops/projects/${id}/resync`);
          setProjects(prev => prev.map(p => p.id === id ? res.data.data : p));
          setLaborProject(res.data.data);
        }}
      />

      {/* Expense Ledger Modal */}
      <ExpenseLedgerModal
        open={!!expenseProject}
        onOpenChange={(o) => { if (!o) setExpenseProject(null); }}
        project={expenseProject}
        onSave={handleSaveExpenses}
      />

      {/* Revenue Ledger Modal */}
      <RevenueLedgerModal
        open={!!revenueProject}
        onOpenChange={(o) => { if (!o) setRevenueProject(null); }}
        project={revenueProject}
        onSave={handleSaveRevenue}
      />

      {/* Economics Modal */}
      <EconomicsModal
        open={!!economicsProject}
        onOpenChange={(o) => { if (!o) setEconomicsProject(null); }}
        project={economicsProject}
      />

      {/* Trackabi key settings */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle data-role="heading">Trackabi API Key</DialogTitle></DialogHeader>
          <div className="space-y-3 py-3">
            <p className="text-xs text-muted-foreground">Current: <span data-role="mono">{maskedKey || 'Not set'}</span></p>
            <Input type="text" value={trackKey} onChange={(e) => setTrackKey(e.target.value)} placeholder="Paste new API key" data-testid="trackabi-key-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveTrackKey} disabled={savingKey || !trackKey}>
              {savingKey ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}Save Key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
